import dayjs from 'dayjs';
import FuzzySearch from 'fuzzy-search';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { apiConfigs } from '../../../api/APIConfigs';
import { RhEvent } from '../../../api/models/RhEvent';
import { eventSlice } from '../../../model/eventSlice';
import { RootState } from '../../../model/store';
import { routes } from '../../../route-list';
import { useAsyncRequest } from '../../../util/use-async-request';
import { useRequest } from '../../../util/use-request';
import { RhTab } from '../../core/RhTabs';
import { EventsScreen } from '../components/EventsScreen';

export type EventsProps = {
  // Inputs
  /** current tab id on the uri */
  currentTabId: string;
  /** tabs of this view */
  tabs: RhTab[];
  /** list of scheduled events */
  scheduledEvents: RhEvent[];
  /** lsit of past events */
  pastEvents: RhEvent[];
  /** if getting events */
  loading: boolean;

  // Inputs
  /** called when the user clicks the add button */
  userClickedAddEvent: () => void;
  /** called when the user clicks a tab */
  userSelectedTab: (id: string) => void;
  /** called when user clicks a single event from the list */
  userClickedAnEvent: (id: string) => void;
  /** called when the user edits the search field on the scheduled live events */
  userUpdatedScheduledLiveEventsSearchField: (term: string) => void;
  /** called when the user edits the search field on the past live events */
  userUpdatedPastLiveEventsSearchField: (term: string) => void;
};

export const EventsContainer = () => {
  const events = useSelector((state: RootState) => state.event.list);

  const [currentTabId, setCurrentTabId] = useState('all');
  const [scheduledLiveEventsSearchTerm, setScheduledLiveEventsSearchTerm] = useState('');
  const [pastLiveEventsSearchTerm, setPastLiveEventsSearchTerm] = useState('');

  const request = useRequest();
  const dispatch = useDispatch();
  const history = useHistory();

  // get list of events
  const { loading: isGettingEvents } = useAsyncRequest(
    useCallback(() => request<RhEvent[]>(apiConfigs.getEvents({ showOnlyFromCurrentUser: true })), [request]),
    useCallback(events => dispatch(eventSlice.actions.receivedList({ events })), [dispatch])
  );

  const scheduledEvents = useMemo(() => {
    return events.filter(e => e.endTime && dayjs(e.endTime).isSameOrAfter(new Date()));
  }, [events]);

  const pastEvents = useMemo(() => {
    return events.filter(e => e.endTime && dayjs(e.endTime).isBefore(new Date()));
  }, [events]);

  const scheduledLiveEventsSearcher = new FuzzySearch(scheduledEvents, ['title', 'description', 'dateCreated']);
  const pastLiveEventsSearcher = new FuzzySearch(pastEvents, ['title', 'description', 'dateCreated']);

  return (
    <EventsScreen
      loading={isGettingEvents}
      tabs={[
        { id: 'all', title: 'All' },
        { id: 'event-leaders', title: 'Event Leaders' },
        { id: 'content-creators', title: 'Content Creators' },
        { id: 'church-admins', title: 'Church Admins' },
      ]}
      scheduledEvents={scheduledLiveEventsSearcher.search(scheduledLiveEventsSearchTerm)}
      pastEvents={pastLiveEventsSearcher.search(pastLiveEventsSearchTerm)}
      currentTabId={currentTabId}
      userClickedAddEvent={() => {
        history.push(routes.EVENTS__ADD);
      }}
      userSelectedTab={id => {
        setCurrentTabId(id);
      }}
      userClickedAnEvent={id => {
        history.push(routes.EVENTS__VIEW(id));
      }}
      userUpdatedPastLiveEventsSearchField={setPastLiveEventsSearchTerm}
      userUpdatedScheduledLiveEventsSearchField={setScheduledLiveEventsSearchTerm}
    />
  );
};
